import { useState, useEffect } from 'react';
import { getApi, postApi } from '../../utils/apiService';
import { GET_ASSESSMENT_DETAILS, GET_GRADES, GET_BRANCHES } from '../../utils/apiPath';
import { useNavigate, useParams } from 'react-router-dom';
import InputGroup from '../../components/InputGroup';
import Button from '../../components/Button';
import {
    ArrowRight,
    GraduationCap,
    Clock,
    FileText,
    Target,
    LayoutGrid,
    Loader2,
    AlertCircle,
    BookOpen,
    ShieldAlert,
    Mail,
    CalendarClock,
    RefreshCw,
    Lock
} from 'lucide-react';
import { APP_LOGO as logo } from '../../utils/appAssets';
import './Home.scss';

// ---------------------------------------------------------------------------
// Parse scheduled datetime from API response.
// API returns: scheduledDate ("2026-04-15") + scheduledTime ("18:40:00")
// Also supports a combined scheduledDateTime ISO string as a fallback.
// ---------------------------------------------------------------------------
const parseScheduleDateTime = (details) => {
    if (!details) return null;
    if (details.scheduledDate && details.scheduledTime) {
        const dt = new Date(`${details.scheduledDate}T${details.scheduledTime}`);
        return isNaN(dt.getTime()) ? null : dt;
    }
    if (details.scheduledDateTime) {
        const dt = new Date(details.scheduledDateTime);
        return isNaN(dt.getTime()) ? null : dt;
    }
    return null;
};

const formatDisplayDateTime = (dt) => {
    if (!dt) return { date: '', time: '' };
    const date = dt.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    const time = dt.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
    return { date, time };
};

const Home = () => {
    const { assessmentTypeId, gradeId, scheduleId, branchId  } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        studentName: '',
        parentEmail: '',
        section: '',
        dob: '',
        academicYear: '',
        branchName: ''
    });
    const [errors, setErrors] = useState({});
    const [assessmentDetails, setAssessmentDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [gradeName, setGradeName] = useState('');
    const [now, setNow] = useState(() => new Date());

    // Section Options
    const sectionOptions = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

    // Branch Options
    const [branchOptions, setBranchOptions] = useState([]);

    // Generate Year Options
    const currentYear = new Date().getFullYear();
    const yearOptions = [
        `${currentYear - 1}-${currentYear}`,
        `${currentYear}-${currentYear + 1}`,
        `${currentYear + 1}-${currentYear + 2}`,
        `${currentYear + 2}-${currentYear + 3}`
    ];

    // Today's date for DOB restriction
    const today = new Date().toISOString().split('T')[0];

    // Tick every 30 s so schedule state stays current without a full-page refresh
    useEffect(() => {
        const intervalId = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(intervalId);
    }, []);

    // Fetch Assessment Details
    useEffect(() => {
        if (!assessmentTypeId) return;
        const fetchDetails = async () => {
            setLoading(true);
            setFetchError('');
            try {
                const response = await getApi(GET_ASSESSMENT_DETAILS, { params: { id: assessmentTypeId, scheduleId: scheduleId ? Number(scheduleId) : null } });
                if (response?.data) {
                    setAssessmentDetails(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch assessment details', error);
                setFetchError('Could not load assessment details.');
            } finally {
                setLoading(false);
                setNow(new Date()); // re-evaluate schedule immediately after fetch
            }
        };
        fetchDetails();
    }, [assessmentTypeId]);

    // Fetch Grade Name
    useEffect(() => {
        if (!gradeId) return;
        const fetchGrades = async () => {
            try {
                const response = await postApi(GET_GRADES);
                if (response?.data && Array.isArray(response.data)) {
                    const matchedGrade = response.data.find(item => String(item.id) === String(gradeId));
                    if (matchedGrade) {
                        setGradeName(matchedGrade.name);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch grades", error);
            }
        };
        fetchGrades();
    }, [gradeId]);

    // Fetch Branches
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await getApi(GET_BRANCHES);
                if (response?.data && Array.isArray(response.data)) {
                    setBranchOptions(response.data);
                } else if (response?.data?.data && Array.isArray(response.data.data)) {
                    setBranchOptions(response.data.data);
                } else if (response?.data?.items && Array.isArray(response.data.items)) {
                    setBranchOptions(response.data.items);
                } else if (Array.isArray(response)) {
                    setBranchOptions(response);
                }
            } catch (error) {
                console.error("Failed to fetch branches", error);
            }
        };
        fetchBranches();
    }, []);

    // Auto-select branch if branchId is present in URL
    useEffect(() => {
        if (branchId && branchOptions.length > 0) {
            const matchedBranch = branchOptions.find(b => String(b.value) === String(branchId));
            if (matchedBranch) {
                setFormData(prev => ({ ...prev, branchName: matchedBranch.value }));
            }
        }
    }, [branchId, branchOptions]);

    // Derive schedule state from assessment details + current time.
    // durationInMin may arrive as a string from the API — always parseInt before arithmetic.
    const scheduleDateTime = parseScheduleDateTime(assessmentDetails);
    const durationMin = parseInt(assessmentDetails?.durationInMin || 0, 10);
    const deadlineDateTime = scheduleDateTime && !isNaN(durationMin)
        ? new Date(scheduleDateTime.getTime() + (durationMin + 10) * 60 * 1000)
        : null;
    const isScheduleActive = !scheduleDateTime || now >= scheduleDateTime;
    const isDeadlinePassed = deadlineDateTime ? now >= deadlineDateTime : false;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.studentName.trim()) newErrors.studentName = 'Student Name is required';
        if (!formData.parentEmail.trim()) {
            newErrors.parentEmail = 'Parent Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.parentEmail)) {
            newErrors.parentEmail = 'Please enter a valid email address';
        }
        if (!formData.section?.trim()) newErrors.section = 'Section is required';
        if (!formData.dob) newErrors.dob = 'Date of Birth is required';
        if (!formData.academicYear) newErrors.academicYear = 'Please select an Academic Year';
        if (!formData.branchName) newErrors.branchName = 'Please select a Branch';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // No assessment context — show fallback screen
    if (!assessmentTypeId && !gradeId) {
        return (
            <div className="home-page fade-in">
                <div className="no-assessment-card slide-up">
                    <div className="no-assessment-card__brand">
                        <img src={logo} alt="Meridian School" className="no-assessment-card__logo" />
                    </div>

                    <div className="no-assessment-card__body">
                        <div className="no-assessment-card__icon-wrap">
                            <ShieldAlert size={48} strokeWidth={1.5} />
                        </div>
                        <h1 className="no-assessment-card__title">No Assessment Available</h1>
                        <p className="no-assessment-card__message">
                            Welcome, Meridian student! It looks like no assessment has been assigned to you yet,
                            or the link you used is incomplete.
                        </p>
                        <div className="no-assessment-card__divider" />
                        <div className="no-assessment-card__contact">
                            <Mail size={16} />
                            <span>Please contact your <strong>administrator</strong> to get the correct assessment link.</span>
                        </div>
                    </div>

                    <div className="no-assessment-card__footer">
                        <strong>Meridian</strong>
                    </div>
                </div>
            </div>
        );
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            const selectedBranch = branchOptions.find(b => String(b.value) === String(formData.branchName));
            
            const studentDataToStore = {
                ...formData,
                branchId: selectedBranch ? selectedBranch.value : (branchId ? Number(branchId) : null),
                branchName: selectedBranch ? selectedBranch.label : formData.branchName

            };

            sessionStorage.setItem('assessmentStudentData', JSON.stringify(studentDataToStore));
            sessionStorage.setItem('assessmentStartTime', Date.now().toString());
            sessionStorage.setItem('assessmentDurationMin', String(durationMin));
            // Carry the schedule's instruction flag to the Assessment page so it can
            // decide whether to show the General Instructions screen before the modules.
            sessionStorage.setItem(
                'assessmentGenerateInstruction',
                assessmentDetails?.generateInstruction ? 'true' : 'false'
            );
            // Fresh start — clear any prior acknowledgement so instructions show again.
            sessionStorage.removeItem('assessmentInstructionsAck');
            if (assessmentTypeId) {
                // Build the path from only the segments that actually exist so we
                // never stamp "undefined" into the URL when a param is missing.
                const segments = [assessmentTypeId, gradeId, scheduleId, branchId].filter(
                    (seg) => seg !== undefined && seg !== null && seg !== ''
                );
                navigate(`/assessment/${segments.join('/')}`);
            } else {
                navigate(`/assessment/${formData.grade}`);
            }
        }
    };

    // Assessment window has fully closed (schedule end + 10 min buffer elapsed)
    if (!loading && assessmentDetails && isDeadlinePassed) {
        return (
            <div className="home-page fade-in">
                <div className="no-assessment-card slide-up">
                    <div className="no-assessment-card__brand">
                        <img src={logo} alt="Meridian School" className="no-assessment-card__logo" />
                    </div>

                    <div className="no-assessment-card__body">
                        <div className="no-assessment-card__icon-wrap no-assessment-card__icon-wrap--closed">
                            <Lock size={48} strokeWidth={1.5} />
                        </div>
                        <h1 className="no-assessment-card__title">Assessment Window Closed</h1>
                        <p className="no-assessment-card__message">
                            The assessment window for <strong>{assessmentDetails.name}</strong> has ended.
                            No further submissions are being accepted.
                        </p>
                        <div className="no-assessment-card__divider" />
                        <div className="no-assessment-card__contact">
                            <Mail size={16} />
                            <span>Please contact your <strong>administrator</strong> for further assistance.</span>
                        </div>
                    </div>

                    <div className="no-assessment-card__footer">
                        <strong>Meridian</strong>
                    </div>
                </div>
            </div>
        );
    }

    // Assessment not yet available — schedule is in the future
    if (!loading && assessmentDetails && !isScheduleActive) {
        const { date: scheduledDate, time: scheduledTime } = formatDisplayDateTime(scheduleDateTime);
        return (
            <div className="home-page fade-in">
                <div className="no-assessment-card slide-up">
                    <div className="no-assessment-card__brand">
                        <img src={logo} alt="Meridian School" className="no-assessment-card__logo" />
                    </div>

                    <div className="no-assessment-card__body">
                        <div className="no-assessment-card__icon-wrap no-assessment-card__icon-wrap--schedule">
                            <CalendarClock size={48} strokeWidth={1.5} />
                        </div>
                        <h1 className="no-assessment-card__title">Assessment Not Yet Available</h1>
                        <p className="no-assessment-card__message">
                            <strong>{assessmentDetails.name}</strong> is scheduled and will be available at:
                        </p>

                        <div className="no-assessment-card__schedule-time">
                            <div className="no-assessment-card__schedule-time-value">{scheduledTime}</div>
                            <div className="no-assessment-card__schedule-time-date">{scheduledDate}</div>
                        </div>

                        <div className="no-assessment-card__divider" />

                        <div className="no-assessment-card__refresh-hint">
                            <RefreshCw size={16} />
                            <span>
                                If the scheduled time has already passed, please <strong>refresh this page</strong> to access the assessment.
                            </span>
                        </div>
                    </div>

                    <div className="no-assessment-card__footer">
                        <strong>Meridian</strong>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="home-page fade-in">
            <div className="home-wrapper slide-up">

                {/* ── LEFT: Assessment Info Panel ── */}
                <div className="info-panel">
                    <div className="info-panel__header">
                        <div className="info-panel__logo-wrap">
                            <img src={logo} alt="Meridian School" className="info-panel__logo" />
                        </div>
                        <h1 className="info-panel__title">Student Assessment</h1>
                        <p className="info-panel__tagline">Enter your details on the right to begin the evaluation.</p>
                    </div>

                    {loading && (
                        <div className="info-loading">
                            <Loader2 size={28} className="spin" />
                            <span>Loading assessment info…</span>
                        </div>
                    )}

                    {fetchError && !loading && (
                        <div className="info-error">
                            <AlertCircle size={18} />
                            <span>{fetchError}</span>
                        </div>
                    )}

                    {assessmentDetails && !loading && (
                        <div className="info-details fade-in">
                            <div className="info-details__name-block">
                                <h2 className="info-details__name">{assessmentDetails.name}</h2>
                                {assessmentDetails.subTitle && (
                                    <span className="info-details__subtitle">{assessmentDetails.subTitle}</span>
                                )}
                            </div>

                            {assessmentDetails.description && (
                                <p className="info-details__description">{assessmentDetails.description}</p>
                            )}

                            <div className="info-stats">
                                {gradeName && (
                                    <div className="info-stat-chip">
                                        <GraduationCap size={15} />
                                        <span>Grade: {gradeName}</span>
                                    </div>
                                )}
                                {assessmentDetails.subject && (
                                    <div className="info-stat-chip">
                                        <BookOpen size={15} />
                                        <span>Subject: {assessmentDetails.subject}</span>
                                    </div>
                                )}
                                {assessmentDetails.durationInMin && (
                                    <div className="info-stat-chip">
                                        <Clock size={15} />
                                        <span>{assessmentDetails.durationInMin} min</span>
                                    </div>
                                )}
                                {assessmentDetails.format && (
                                    <div className="info-stat-chip">
                                        <LayoutGrid size={15} />
                                        <span>{assessmentDetails.format}</span>
                                    </div>
                                )}
                            </div>

                            {assessmentDetails.purpose && (
                                <div className="info-row">
                                    <div className="info-row__icon"><Target size={16} /></div>
                                    <div>
                                        <span className="info-row__label">Purpose</span>
                                        <p className="info-row__value">{assessmentDetails.purpose}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!assessmentDetails && !loading && !fetchError && (
                        <div className="info-placeholder">
                            <FileText size={40} />
                            <p>Assessment details will appear here.</p>
                        </div>
                    )}

                    <div className="info-panel__footer">
                        <span><strong>Meridian</strong></span>
                    </div>
                </div>

                {/* ── RIGHT: Form Panel ── */}
                <div className="form-panel">
                    <div className="form-panel__inner">
                        <h2 className="form-panel__heading">Your Details</h2>
                        <p className="form-panel__sub">Fill in the information below to get started.</p>

                        <form onSubmit={handleSubmit} noValidate>
                            <div className="fade-in-item" style={{ animationDelay: '0.1s' }}>
                                <InputGroup
                                    label="Student Name"
                                    name="studentName"
                                    placeholder="e.g. John Doe"
                                    value={formData.studentName}
                                    onChange={handleChange}
                                    error={errors.studentName}
                                    required
                                />
                            </div>

                            <div className="fade-in-item" style={{ animationDelay: '0.2s' }}>
                                <InputGroup
                                    label="Parent Email Id"
                                    name="parentEmail"
                                    type="email"
                                    placeholder="e.g. parent@example.com"
                                    value={formData.parentEmail}
                                    onChange={handleChange}
                                    error={errors.parentEmail}
                                    required
                                />
                            </div>

                            <div className="fade-in-item" style={{ animationDelay: '0.3s' }}>
                                <InputGroup
                                    label="Section"
                                    name="section"
                                    type="select"
                                    placeholder="Select Section"
                                    options={sectionOptions}
                                    value={formData.section}
                                    onChange={handleChange}
                                    error={errors.section}
                                    required
                                />
                            </div>

                            <div className="fade-in-item" style={{ animationDelay: '0.4s' }}>
                                <InputGroup
                                    label="Date of Birth"
                                    name="dob"
                                    type="date"
                                    max={today}
                                    value={formData.dob}
                                    onChange={handleChange}
                                    error={errors.dob}
                                    required
                                />
                            </div>

                            <div className="fade-in-item" style={{ animationDelay: '0.5s' }}>
                                <InputGroup
                                    label="Academic Year"
                                    name="academicYear"
                                    type="select"
                                    options={yearOptions}
                                    value={formData.academicYear}
                                    onChange={handleChange}
                                    error={errors.academicYear}
                                    required
                                />
                            </div>

                            <div className="fade-in-item" style={{ animationDelay: '0.6s' }}>
                                <InputGroup
                                    label="Branch"
                                    name="branchName"
                                    type="select"
                                    placeholder="Select Branch"
                                    options={branchOptions}
                                    value={formData.branchName}
                                    onChange={handleChange}
                                    error={errors.branchName}
                                    required
                                />
                            </div>

                            <div className="fade-in-item form-actions" style={{ animationDelay: '0.7s' }}>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setFormData({ studentName: '', parentEmail: '', section: '', dob: '', academicYear: '', branchName: '' });
                                        setErrors({});
                                    }}
                                    style={{ flex: 1 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    style={{ flex: 1 }}
                                >
                                    Start Assessment <ArrowRight size={18} />
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Home;
